/**
 * TRIL-277: shared match-predicate factories for feed-cache eviction
 * (`evictPostsFromFeedCache`). Extracted from `recommendationFeed`'s five
 * inline predicates, which copy-pasted the post-id match across the two hide
 * sites and the 4-field author resolution across mute + block. Centralising
 * them gives a single source of truth so the author-field resolution can't
 * drift between handlers. Behavior-preserving.
 *
 * Each factory returns a predicate over the candidate shape that
 * `evictPostsFromFeedCache` builds:
 * `{ postId, id, rootPostId, userId, authorId, author }`.
 */

// Matches the loose candidate shape read out of the Apollo feed cache.
// biome-ignore lint/suspicious/noExplicitAny: feed-cache candidate is loosely typed
type FeedPostCandidate = Record<string, any>;

/** Matches a single post by its id (either `postId` or `id`). */
export const matchFeedPostById =
  (postId: string) =>
  (p: FeedPostCandidate): boolean =>
    p.postId === postId || p.id === postId;

/** Matches every post in a thread by its root post id. */
export const matchFeedPostByThread =
  (rootPostId: string) =>
  (p: FeedPostCandidate): boolean =>
    p.rootPostId === rootPostId ||
    p.postId === rootPostId ||
    p.id === rootPostId;

/**
 * Matches every post by a given author. The author id is resolved from the
 * first present of: `author.authorId`, `author.userId`, `userId`, `authorId`.
 */
export const matchFeedPostByAuthor =
  (authorId: string) =>
  (p: FeedPostCandidate): boolean => {
    const postAuthorId =
      p.author?.authorId ?? p.author?.userId ?? p.userId ?? p.authorId;
    return postAuthorId === authorId;
  };
